'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Mail, Lock, User, Phone, Sparkles, ArrowRight, CheckCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  phone?: string;
  password?: string;
  confirmPassword?: string;
}

function getPasswordStrength(password: string): { level: number; label: string; color: string } {
  if (!password) return { level: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 6) score++;
  if (password.length >= 10) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  if (score <= 1) return { level: 1, label: 'Weak', color: 'bg-red-500' };
  if (score === 2) return { level: 2, label: 'Fair', color: 'bg-yellow-500' };
  if (score === 3) return { level: 3, label: 'Good', color: 'bg-saffron' };
  return { level: 4, label: 'Strong', color: 'bg-green-500' };
}

export default function RegisterPage() {
  const router = useRouter();
  const { signUp } = useAuth();

  const [form, setForm] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [success, setSuccess] = useState(false);

  const passwordStrength = getPasswordStrength(form.password);

  const updateField = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
    setApiError('');
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.fullName.trim()) newErrors.fullName = 'Full name is required.';
    else if (form.fullName.trim().length < 2) newErrors.fullName = 'Name must be at least 2 characters.';

    if (!form.email.trim()) newErrors.email = 'Email address is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Please enter a valid email address.';

    if (form.phone && !/^[+\d\s\-()]{7,15}$/.test(form.phone)) {
      newErrors.phone = 'Please enter a valid phone number.';
    }

    if (!form.password) newErrors.password = 'Password is required.';
    else if (form.password.length < 6) newErrors.password = 'Password must be at least 6 characters.';

    if (!form.confirmPassword) newErrors.confirmPassword = 'Please confirm your password.';
    else if (form.password !== form.confirmPassword) newErrors.confirmPassword = 'Passwords do not match.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setApiError('');
    setLoading(true);
    try {
      await signUp(form.email.trim(), form.password, {
        fullName: form.fullName.trim(),
        phone: form.phone.trim() || undefined,
      });
      setSuccess(true);
      setTimeout(() => {
        router.push('/home-screen');
        router.refresh();
      }, 1800);
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already exists')) {
        setApiError('An account with this email already exists. Please login instead.');
      } else if (msg.toLowerCase().includes('password')) {
        setApiError('Password does not meet requirements. Please choose a stronger password.');
      } else {
        setApiError(msg || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center px-4">
        <div className="text-center animate-scale-in">
          <div className="w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-400/50 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-400" />
          </div>
          <h2 className="font-display text-3xl font-bold text-ivory mb-2">Account Created!</h2>
          <p className="text-ivory/60 font-body">Welcome to Yagnick. Redirecting you…</p>
          <div className="mt-4 flex justify-center">
            <div className="w-6 h-6 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">
      {/* Mandala Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-10 animate-rotate-slow"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='200' cy='200' r='190' fill='none' stroke='%23D4AF37' stroke-width='1'/%3E%3Ccircle cx='200' cy='200' r='150' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3Ccircle cx='200' cy='200' r='110' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3Ccircle cx='200' cy='200' r='70' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3Ccircle cx='200' cy='200' r='35' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3C/svg%3E")`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
          }}
        />
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-saffron/15 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-gold/10 blur-3xl" />
        {[...Array(6)]?.map((_, i) => (
          <div
            key={i}
            className="absolute w-1.5 h-1.5 rounded-full bg-gold/50 animate-float"
            style={{
              left: `${8 + i * 16}%`,
              top: `${10 + (i % 3) * 30}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${3 + (i % 2)}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 w-full max-w-md animate-fade-in-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link href="/login" className="inline-flex flex-col items-center gap-3">
            <div className="w-16 h-16 rounded-full saffron-gradient flex items-center justify-center shadow-xl glow-saffron animate-float">
              <span className="text-3xl diya-glow" role="img" aria-label="Diya lamp">🪔</span>
            </div>
            <span className="font-display text-3xl font-bold text-ivory tracking-tight">Yagnick</span>
          </Link>
          <p className="mt-2 text-ivory/60 text-sm font-body">Sacred Rituals, Verified Priests</p>
        </div>

        <div className="glass-dark rounded-3xl p-8 shadow-2xl border border-gold/30">
          {/* Header */}
          <div className="text-center mb-7">
            <div className="inline-flex items-center gap-2 bg-gold/20 border border-gold/30 text-gold text-xs font-medium px-3 py-1.5 rounded-full mb-3 tracking-wider uppercase">
              <Sparkles size={11} />
              Join Yagnick
            </div>
            <h1 className="font-display text-3xl font-bold text-ivory">Create Your Account</h1>
            <p className="text-ivory/60 text-sm mt-1 font-body">Begin your sacred journey today</p>
          </div>

          {/* API Error */}
          {apiError && (
            <div className="mb-5 bg-red-900/30 border border-red-500/40 rounded-2xl px-4 py-3 animate-scale-in">
              <p className="text-red-300 text-sm font-body">{apiError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate aria-label="Create account form">
            {/* Full Name */}
            <div>
              <label htmlFor="reg-fullname" className="block text-ivory/80 text-sm font-medium mb-2 font-body">Full Name</label>
              <div className="relative">
                <User size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gold/60 pointer-events-none" aria-hidden="true" />
                <input
                  id="reg-fullname"
                  type="text"
                  value={form.fullName}
                  onChange={(e) => updateField('fullName', e.target.value)}
                  placeholder="Arjun Sharma"
                  className={`w-full bg-white/8 border ${errors.fullName ? 'border-red-500/60' : 'border-gold/25 hover:border-gold/50 focus:border-gold'} text-ivory placeholder-ivory/30 rounded-2xl pl-11 pr-4 py-3.5 text-sm font-body outline-none transition-all duration-200 focus:bg-white/12`}
                  autoComplete="name"
                  required
                  aria-describedby={errors.fullName ? 'reg-fullname-error' : undefined}
                  aria-invalid={!!errors.fullName}
                />
              </div>
              {errors.fullName && <p id="reg-fullname-error" className="text-red-400 text-xs mt-1.5 font-body" role="alert">{errors.fullName}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="reg-email" className="block text-ivory/80 text-sm font-medium mb-2 font-body">Email Address</label>
              <div className="relative">
                <Mail size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gold/60 pointer-events-none" aria-hidden="true" />
                <input
                  id="reg-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="you@example.com"
                  className={`w-full bg-white/8 border ${errors.email ? 'border-red-500/60' : 'border-gold/25 hover:border-gold/50 focus:border-gold'} text-ivory placeholder-ivory/30 rounded-2xl pl-11 pr-4 py-3.5 text-sm font-body outline-none transition-all duration-200 focus:bg-white/12`}
                  autoComplete="email"
                  required
                  aria-describedby={errors.email ? 'reg-email-error' : undefined}
                  aria-invalid={!!errors.email}
                />
              </div>
              {errors.email && <p id="reg-email-error" className="text-red-400 text-xs mt-1.5 font-body" role="alert">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="reg-phone" className="block text-ivory/80 text-sm font-medium mb-2 font-body">
                Phone Number <span className="text-ivory/40 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <Phone size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gold/60 pointer-events-none" aria-hidden="true" />
                <input
                  id="reg-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder="+91 98765 43210"
                  className={`w-full bg-white/8 border ${errors.phone ? 'border-red-500/60' : 'border-gold/25 hover:border-gold/50 focus:border-gold'} text-ivory placeholder-ivory/30 rounded-2xl pl-11 pr-4 py-3.5 text-sm font-body outline-none transition-all duration-200 focus:bg-white/12`}
                  autoComplete="tel"
                  aria-describedby={errors.phone ? 'reg-phone-error' : undefined}
                  aria-invalid={!!errors.phone}
                />
              </div>
              {errors.phone && <p id="reg-phone-error" className="text-red-400 text-xs mt-1.5 font-body" role="alert">{errors.phone}</p>}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="reg-password" className="block text-ivory/80 text-sm font-medium mb-2 font-body">Password</label>
              <div className="relative">
                <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gold/60 pointer-events-none" aria-hidden="true" />
                <input
                  id="reg-password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => updateField('password', e.target.value)}
                  placeholder="Min. 6 characters"
                  className={`w-full bg-white/8 border ${errors.password ? 'border-red-500/60' : 'border-gold/25 hover:border-gold/50 focus:border-gold'} text-ivory placeholder-ivory/30 rounded-2xl pl-11 pr-12 py-3.5 text-sm font-body outline-none transition-all duration-200 focus:bg-white/12`}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-ivory/40 hover:text-gold transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
              {/* Password strength */}
              {form.password && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex gap-1 flex-1">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                          level <= passwordStrength.level ? passwordStrength.color : 'bg-white/15'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-ivory/50 font-body">{passwordStrength.label}</span>
                </div>
              )}
              {errors.password && <p className="text-red-400 text-xs mt-1.5 font-body">{errors.password}</p>}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-ivory/80 text-sm font-medium mb-2 font-body">Confirm Password</label>
              <div className="relative">
                <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gold/60" />
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={(e) => updateField('confirmPassword', e.target.value)}
                  placeholder="Re-enter your password"
                  className={`w-full bg-white/8 border ${errors.confirmPassword ? 'border-red-500/60' : 'border-gold/25 hover:border-gold/50 focus:border-gold'} text-ivory placeholder-ivory/30 rounded-2xl pl-11 pr-12 py-3.5 text-sm font-body outline-none transition-all duration-200 focus:bg-white/12`}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-ivory/40 hover:text-gold transition-colors"
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                >
                  {showConfirm ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
              {errors.confirmPassword && <p className="text-red-400 text-xs mt-1.5 font-body">{errors.confirmPassword}</p>}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full saffron-gradient text-white font-semibold py-4 rounded-2xl btn-glow flex items-center justify-center gap-2 text-base mt-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating Account…
                </>
              ) : (
                <>
                  Create Account
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Login link */}
          <p className="text-center text-ivory/50 text-sm mt-6 font-body">
            Already have an account?{' '}
            <Link href="/login" className="text-gold hover:text-saffron font-semibold transition-colors">
              Login
            </Link>
          </p>
        </div>

        <p className="text-center text-ivory/30 text-xs mt-6 font-body">
          🙏 Connecting devotees with verified priests across India
        </p>
      </div>
    </div>
  );
}
